import type { AccountModel } from '@app/models/api/AccountModel';
import type { NotificationModel, Notifications } from '@app/models/app/NotificationsModel';
import { getAccountStatus } from '@app/utils/user';
import { urls } from '@app/urls';
import type { SubscriptionModel } from '@app/models/api/SubscriptionModel';
import { SignatureStatusEnum } from '@app/models/api/enums/SignatureStatusEnum';
import { SignatoryStatusEnum } from '@app/models/api/enums/SignatoryStatusEnum';
import { type MessageFormatter, t } from 'svelte-i18n';
import { get } from 'svelte/store';
import type { PollModel } from '@app/models/api/PollModel';
import { formatDateTimeWithLocale, isDateBefore } from '@wiseed/wikit';
import { getSubscriptionsByOperation } from '@app/services/api/subscriptions';
import type { SubscriptionCollectionModel } from '@app/models/api/SubscriptionCollectionModel';
import type { Fetch } from '@app/types/Global';
import type { GeneralAssemblyModel } from '@app/models/api/GeneralAssemblyModel';
import { AccountStateEnum } from '@wiseed/wikit';
const getConversionsNotifications = (
	conversions: SubscriptionModel[],
	$t: MessageFormatter
): Notifications => {
	conversions = conversions.filter(
		(conversion) =>
			conversion.proxySignatureOCA.status !== SignatureStatusEnum.STATUT_EXPIRE &&
			(!conversion.proxySignatureOCA?.signatories ||
				conversion.proxySignatureOCA?.signatories[0]?.status !== SignatoryStatusEnum.STATUT_SIGNE)
	);
	return conversions.map((c): NotificationModel => {
		return {
			iconName: 'wallet',
			imageUrl: c.operation.logo ?? c.operation.card,
			title: c.operation.name,
			desc: $t('account.dashboard.OCA.notification'),
			linkLabel: $t('clickHere'),
			linkUrl: `${urls.USER.PORTFOLIO.DASHBOARD}#conversions`,
			smoothScroll: '#conversions'
		};
	});
};

const getPendingSubscriptionsNotifications = (
	pendingSubscriptionsToSign: SubscriptionModel[],
	$t,
	isToPay?: boolean
): Notifications => {
	return pendingSubscriptionsToSign.map((s: SubscriptionModel): NotificationModel => {
		const missingSubscriptionInformations = s.isOPC && !s.isInfosFilled;
		return {
			iconName: 'wallet',
			imageUrl: s.operation.logo ?? s.operation.card,
			title: s.operation.name,
			desc: missingSubscriptionInformations
				? $t('portfolio.pendingSubscriptions.informationsNotification')
				: isToPay
					? $t('portfolio.pendingSubscriptions.paymentNotification')
					: $t('portfolio.pendingSubscriptions.signatureNotification'),
			linkLabel: $t('clickHere'),
			linkUrl: missingSubscriptionInformations
				? urls.SUBSCRIBE.INFORMATION(s.operation.id.toString(), s.id.toString())
				: urls.SUBSCRIBE.STATUS(s.operation.id.toString(), s.id.toString())
		};
	});
};

export const getPollsNotifications = async (
	polls: PollModel[],
	$t,
	sveltekitFetch: Fetch
): Promise<Notifications> => {
	const pollsToAnswer = polls.filter(
		(poll) => !poll.isAnswered && (isDateBefore(new Date(), poll.dateFin) || !poll.dateFin)
	);

	const promises = pollsToAnswer.map(async (p: PollModel): Promise<NotificationModel> => {
		const relatedSubscriptions: SubscriptionCollectionModel[] = await getSubscriptionsByOperation(
			p.operations[0].id.toString(),
			sveltekitFetch
		);
		const subscriptionId: string = relatedSubscriptions[0].id;
		return {
			iconName: 'wallet',
			title: p.nomCible,
			desc: `${$t('newsFeed.poll.linkPortfolio')}${p.dateFin ? $t('newsFeed.poll.notificationDate', { values: { date: formatDateTimeWithLocale(p.dateFin) } }) : ''}`,
			linkLabel: $t('clickHere'),
			linkUrl: urls.PORTFOLIO.POLLS(subscriptionId, p.id)
		};
	});

	let pollsNotifications: Notifications = [];

	await Promise.allSettled(promises).then((results) =>
		results.forEach((result): void => {
			if (result.status === 'fulfilled') {
				pollsNotifications.push(result.value);
			}
		})
	);

	return pollsNotifications;
};

export const getGeneralAssembliesNotifications = async (
	assemblies: GeneralAssemblyModel[],
	$t,
	sveltekitFetch: Fetch
): Promise<Notifications> => {
	const promises = assemblies.map(async (a: GeneralAssemblyModel): Promise<NotificationModel> => {
		const relatedSubscriptions: SubscriptionCollectionModel[] = await getSubscriptionsByOperation(
			a.linkedOperations[0].id.toString(),
			sveltekitFetch
		);
		const subscriptionId: number = relatedSubscriptions[0].id;
		return {
			iconName: 'wallet',
			title: a.companyName,
			desc: $t('assembly.notification'),
			linkLabel: $t('clickHere'),
			linkUrl: a.isAssembleeWiseed
				? urls.PORTFOLIO.GENERAL_ASSEMBLY_WISEED(a.id)
				: urls.PORTFOLIO.GENERAL_ASSEMBLY(subscriptionId, a.id)
		};
	});

	const assembliesNotifications: Notifications = [];

	await Promise.allSettled(promises).then((results) =>
		results.forEach((result): void => {
			if (result.status === 'fulfilled') {
				assembliesNotifications.push(result.value);
			}
		})
	);

	return assembliesNotifications;
};

export const getUserAccountNotifications = async (
	user?: AccountModel,
	conversions?: SubscriptionModel[],
	pendingSubscriptionsToSign?: SubscriptionModel[],
	pendingSubscriptionsToPay?: SubscriptionModel[],
	pollsNotifications?: Notification[],
	assembliesNotifications?: Notification[]
): Promise<Notifications> => {
	const $t = get(t);
	let notifications: Notifications = [];

	if (user) {
		const userStatus = getAccountStatus(user, $t);

		if (
			userStatus.state === AccountStateEnum.ERROR ||
			userStatus.state === AccountStateEnum.WARNING
		) {
			notifications.push({
				iconName: userStatus.iconName,
				title: userStatus.title,
				desc: userStatus.ctaLabel,
				linkLabel: $t('clickHere'),
				linkUrl: userStatus.ctaLink
			});
		}
	}

	if (conversions?.length) {
		const conversionsNotifications = getConversionsNotifications(conversions, $t);
		notifications = [...notifications, ...conversionsNotifications];
	}

	if (pendingSubscriptionsToSign?.length) {
		const subscriptionsToSignNotifications = getPendingSubscriptionsNotifications(
			pendingSubscriptionsToSign,
			$t
		);
		notifications = [...notifications, ...subscriptionsToSignNotifications];
	}

	if (pendingSubscriptionsToPay?.length) {
		const subscriptionsToPayNotifications = getPendingSubscriptionsNotifications(
			pendingSubscriptionsToPay,
			$t,
			true
		);
		notifications = [...notifications, ...subscriptionsToPayNotifications];
	}

	if (pollsNotifications?.length) {
		notifications = [...notifications, ...pollsNotifications];
	}

	if (assembliesNotifications?.length) {
		notifications = [...notifications, ...assembliesNotifications];
	}

	return notifications;
};
